import {
  createStyles,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Tooltip,
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Close from '@material-ui/icons/Close';
import React, { useState } from 'react';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import { Entity, EntityClassProperty } from '../../../../../../types';
import { JupiterTheme } from '../../../../../theme/types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    container: { width: '100%' },
    input: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    helpIcon: {
      color: theme.palette.grey[400],
      marginBottom: theme.spacing(1),
    },
    adornment: {
      marginRight: 0,
      marginLeft: theme.spacing(0.5),
    },
    tooltip: {
      padding: theme.spacing(2),
      wordBreak: 'break-word',
    },
    inputGroup: {
      display: 'flex',
      marginTop: theme.spacing(2),
    },
    valuesSection: {
      marginBottom: theme.spacing(3),
    },
  })
);

interface Props {
  property: EntityClassProperty;
  entity: Entity;
  onUpdateProperties: (key: string, value: any) => void;
  disabled?: boolean;
}

export const useArrayInput = ({
  entity: { properties },
  onUpdateProperties,
  property,
}: Props) => {
  const [values, setValues] = useState<string[]>(
    properties[property.name]
      ? convertStringToArray(properties[property.name])
      : ['']
  );

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') handleAddValue();
  };

  const handleDeleteValue = (i: number) => {
    return (_e: any) => {
      updateProperty(values.slice(i, 1));
    };
  };

  const handleAddValue = () => {
    const newValues = [...values];
    newValues.push('');
    updateProperty(newValues);
  };

  const handleUpdateExistingValue = (index: number) => {
    return (e: any) => {
      const newValues = [...values];
      newValues[index] = e.target.value;
      updateProperty(newValues);
    };
  };

  const updateProperty = (values: string[]) => {
    onUpdateProperties(property.name, values);
    setValues(values);
  };

  const handleUpdateValue = (e: any) => {
    handleUpdateExistingValue(values.length - 1)(e);
  };
  return {
    values,
    handleUpdateValue,
    handleUpdateExistingValue,
    handleDeleteValue,
    handleKeyPress,
    handleAddValue,
  };
};

function convertStringToArray(arrayString: string | string[]): string[] {
  if (typeof arrayString === 'string') {
    return arrayString.split(',');
  } else {
    return arrayString;
  }
}

const ArrayInput = ({
  onUpdateProperties,
  property,
  disabled = false,
  entity,
  property: { name },
}: Props) => {
  const classes = useStyles();
  const {
    values,
    handleUpdateValue,
    handleUpdateExistingValue,
    handleDeleteValue,
    handleKeyPress,
    handleAddValue,
  } = useArrayInput({
    entity,
    property,
    onUpdateProperties,
  });

  return (
    <div className={classes.container}>
      <TextField
        className={classes.input}
        label={property.name}
        value={values.length ? values[values.length - 1] : ''}
        onChange={handleUpdateValue}
        inputProps={{ name: property.name, 'aria-label': name }}
        name={property.name}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        InputProps={{
          endAdornment: (
            <InputAdornment
              classes={{ root: classes.adornment }}
              position="start"
            >
              <IconButton
                aria-label="Add"
                onClick={handleAddValue}
                disabled={disabled}
              >
                <Add />
              </IconButton>
              <Tooltip
                classes={{ tooltip: classes.tooltip }}
                placement="right"
                title={property.description}
              >
                <div>
                  <HelpIcon className={classes.helpIcon} />
                </div>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      <div>
        {values.slice(0, values.length - 1).map((item, i) => (
          <div className={classes.inputGroup} key={i}>
            <TextField
              value={item}
              onChange={handleUpdateExistingValue(i)}
              name={name + '-label'}
              label={name + '-label'}
              inputProps={{
                'aria-label': name + '-label',
              }}
              className={classes.input}
            />
            <div>
              <IconButton
                aria-label="Remove"
                onClick={handleDeleteValue(i)}
                disabled={disabled}
              >
                <Close />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ArrayInput;
